/**
 * FP3-059 — Solo provider in-app assistant access request modal.
 *
 * Replaces the "email simplyslps@gmail.com" instruction on the
 * Workspace Info Assistant Providers card. POSTs to
 * /api/account/assistant-access-requests; the server stamps
 * organization_id + requesting_user_id/email from the auth context,
 * so the modal never carries cross-org concerns.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UsersThree, X as XIcon } from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";

const TYPE_OPTIONS = [
  { key: "SLPA", label: "SLPA (Speech-Language Pathology Assistant)" },
  { key: "COTA", label: "COTA (Certified Occupational Therapy Assistant)" },
  { key: "PTA", label: "PTA (Physical Therapist Assistant)" },
  { key: "OTHER", label: "Other assistant provider" },
];

export default function AssistantAccessRequestModal({
  open, onOpenChange, onSubmitted,
}) {
  const [count, setCount] = useState(1);
  const [types, setTypes] = useState(["SLPA"]);
  const [assistantName, setAssistantName] = useState("");
  const [assistantEmail, setAssistantEmail] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCount(1);
    setTypes(["SLPA"]);
    setAssistantName("");
    setAssistantEmail("");
    setNote("");
  }, [open]);

  const toggleType = (key) => {
    setTypes((prev) =>
      prev.includes(key) ? prev.filter((t) => t !== key) : [...prev, key]
    );
  };

  const canSubmit = count >= 1 && types.length > 0 && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const r = await api.createAssistantAccessRequest({
        requested_assistant_count: Number(count) || 1,
        requested_assistant_types: types,
        assistant_names: assistantName.trim() ? [assistantName.trim()] : [],
        assistant_emails: assistantEmail.trim() ? [assistantEmail.trim()] : [],
        note: note.trim(),
      });
      toast.success(
        "Assistant access request submitted. We'll review it and follow up."
      );
      onSubmitted?.(r?.data?.request || null);
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.error === "duplicate_pending_request") {
        toast.info("An assistant access request is already pending review.");
        onSubmitted?.({ id: detail.existing_request_id, status: "pending" });
        onOpenChange(false);
        return;
      }
      toast.error(
        (typeof detail === "string" && detail)
          || e?.message
          || "Could not submit request"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="assistant-access-request-modal"
        className="sm:max-w-md bg-white"
      >
        <DialogHeader>
          <DialogTitle className="text-[15px] flex items-center gap-2">
            <UsersThree
              size={16}
              weight="duotone"
              className="text-[#1E3F33]"
            />
            Request assistant access
          </DialogTitle>
          <DialogDescription className="text-[12.5px]">
            Assistant access is for supervised providers — SLPAs, COTAs,
            or PTAs — working under your solo workspace. Requests are
            reviewed before invites are sent.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label
              htmlFor="aar-count"
              className="text-[11px] uppercase tracking-[0.08em] font-semibold text-[#4B5563]"
            >
              Number of assistants <span className="text-[#A8453A]">*</span>
            </label>
            <Select
              value={String(count)}
              onValueChange={(v) => setCount(Number(v) || 1)}
            >
              <SelectTrigger
                id="aar-count"
                data-testid="assistant-access-request-count"
                className="mt-1"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5].map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.08em] font-semibold text-[#4B5563]">
              Assistant type(s) <span className="text-[#A8453A]">*</span>
            </p>
            <div
              className="mt-1 flex flex-wrap gap-1.5"
              data-testid="assistant-access-request-types"
            >
              {TYPE_OPTIONS.map((opt) => {
                const active = types.includes(opt.key);
                return (
                  <button
                    type="button"
                    key={opt.key}
                    onClick={() => toggleType(opt.key)}
                    data-testid={`assistant-access-request-type-${opt.key}`}
                    aria-pressed={active}
                    className={`text-[11.5px] rounded-full px-3 py-1 border transition-colors ${
                      active
                        ? "bg-[#1E3F33] border-[#1E3F33] text-white"
                        : "bg-white border-[#E5E3DB] text-[#3F4756] hover:bg-[#F3F1EC]"
                    }`}
                    title={opt.label}
                  >
                    {opt.key}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label
                htmlFor="aar-name"
                className="text-[11px] uppercase tracking-[0.08em] font-semibold text-[#4B5563]"
              >
                Assistant name (optional)
              </label>
              <Input
                id="aar-name"
                data-testid="assistant-access-request-name"
                value={assistantName}
                onChange={(e) => setAssistantName(e.target.value)}
                maxLength={120}
                placeholder="Jamie Doe"
                className="mt-1 text-[12.5px]"
              />
            </div>
            <div>
              <label
                htmlFor="aar-email"
                className="text-[11px] uppercase tracking-[0.08em] font-semibold text-[#4B5563]"
              >
                Assistant email (optional)
              </label>
              <Input
                id="aar-email"
                data-testid="assistant-access-request-email"
                type="email"
                value={assistantEmail}
                onChange={(e) => setAssistantEmail(e.target.value)}
                maxLength={180}
                placeholder="jamie@example.org"
                className="mt-1 text-[12.5px]"
              />
            </div>
          </div>
          <div>
            <label
              htmlFor="aar-note"
              className="text-[11px] uppercase tracking-[0.08em] font-semibold text-[#4B5563]"
            >
              Note (optional)
            </label>
            <Textarea
              id="aar-note"
              data-testid="assistant-access-request-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={1000}
              rows={3}
              placeholder="Anything we should know before reviewing? No PHI please."
              className="mt-1 text-[12.5px]"
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="assistant-access-request-cancel"
          >
            <XIcon size={12} className="mr-1" /> Cancel
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            data-testid="assistant-access-request-submit"
            className="bg-[#1E3F33] hover:bg-[#173127] text-white disabled:bg-[#1E3F33]/50"
          >
            {submitting ? "Submitting…" : "Submit request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
